/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2020-2024 (original work) Open Assessment Technologies SA
 */

import { __ } from '@oat-sa-private/ui-core';
import { log } from '@/core/utils';

export const DEFAULT_LOCALE = 'en-US';
export const DEFAULT_SUPPORTED_LOCALES = { 'en-US': __('English (USA)') };

export const locale = {
    setDictionaryLoader() {
        __.setDictionaryLoader(async localeCode => {
            try {
                const normalizedLocaleCode = normalizeLocaleCode(localeCode);
                const { default: translation } = await import(`../locale/${normalizedLocaleCode}/messages.po`);
                return translation;
            } catch (err) {
                if (localeCode) {
                    log.warn('Could not load locale of %o', localeCode);
                }
                return {};
            }
        });
        __.setP11nRulesLoader(async localeCode => {
            try {
                const { p11nRules } = await import(`../locale/${localeCode}/messages.po`);
                return p11nRules;
            } catch (err) {
                if (localeCode) {
                    log.warn('Could not load locale plural rules of %o', localeCode);
                }
                return {};
            }
        });

        /**
         * Normalize the locale codes.
         * Case sensitive and insensitive locale codes both are normalized to the format used in the locale folders.
         * For example, 'en-us' or 'en-US' will be both normalized to 'en-US'.
         * Exceptions, like ca-ES-valencia or en-GB-x-excscotl are also properly handled, as first part will be lowercased and second uppercased.
         * @param {string} localeCode
         * @returns {string} string
         */
        function normalizeLocaleCode(localeCode) {
            const [lang, region, ...rest] = localeCode.split('-');
            return [lang.toLowerCase(), region?.toUpperCase(), ...rest].filter(Boolean).join('-');
        }
    },

    /**
     * Set the application locale.
     *
     * @param {String} langCode - unified language key.
     *
     * @returns {Promise} - rejected if code is not supported, resolved otherwise.
     */
    async setApplicationLocale(langCode) {
        await __.setP11nRules(langCode);
        await __.setLocale(langCode);
        await __.setFallbackLocale(DEFAULT_LOCALE);
    },

    /**
     * Gets the lang attribute on html tag
     * @returns {string}
     */
    getLangAttribute: () => document.documentElement.getAttribute('lang'),
    /**
     * Sets the lang attribute on html tag
     *
     * @param {String} langCode - unified language key.
     */
    setLangAttribute(langCode) {
        document.documentElement.setAttribute('lang', langCode);
    },

    /**
     * Sets the locale for the application based on priority
     * @param {string} requestedLocale - The requested locale from the user
     * @returns {Promise<void>}
     */
    async updateApplicationLocale(requestedLocale) {
        await this.setApplicationLocale(requestedLocale);
        this.setLangAttribute(requestedLocale);
    }
};
